import React, { useState } from 'react';
import { ChevronLeft, Plus, Palette, User, Settings, X, Move, RotateCw, Crown, Maximize2, Minimize2, Type } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { useTranslation } from '../contexts/TranslationContext';
import { GameState, CollectibleObject, Hero, Gradients, BackgroundConfig, HeroConfig, HomeObject, UserItemInstance, LogoConfig } from '../types';
import DraggableObject from './DraggableObject';
import SparkleEffect from './SparkleEffect';
import ShinyIndicator from './ShinyIndicator';
import BurningEffect from './BurningEffect';
import ObjectTransformControls from './ObjectTransformControls';
import { useDragAndDrop } from '../hooks/useDragAndDrop';
import { useHeroes } from '../hooks/useHeroes';
import { useCompanions } from '../hooks/useCompanions';
import ItemEffectsRenderer from './ItemEffectsRenderer';
import { getLogoOverlayColor } from '../utils/colorUtils';

interface WorldScreenProps {
  gameState: GameState;
  allObjects: CollectibleObject[];
  userInstances: UserItemInstance[];
  selectedHeroData: Hero | undefined;
  gradients: Gradients;
  onBack: () => void;
  onEditModeToggle: () => void;
  onElementSelect: (element: any) => void;
  onHeroConfigUpdate: (updates: Partial<HeroConfig>) => void;
  onBackgroundConfigUpdate: (config: BackgroundConfig) => void;
  onLogoConfigUpdate: (config: LogoConfig) => void;
  onObjectToHome: (instanceId: number) => void;
  onUpdateHomeObject: (id: number, updates: Partial<HomeObject>) => void;
  onRemoveHomeObject: (id: number) => void;
  onHeroSelect?: (heroId: number) => void;
}

// Composant avancé pour les objets avec drag, resize et rotation
const AdvancedDraggableObject: React.FC<{
  item: HomeObject;
  objectData: CollectibleObject;
  instance?: UserItemInstance;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (id: number, updates: Partial<HomeObject>) => void;
  onRemove: (id: number) => void;
}> = ({ item, objectData, instance, isSelected, onSelect, onUpdate, onRemove }) => {
  const baseSize = 384; // Taille de base haute résolution (objets placés à 25% par défaut = 96px visuel)
  const [isDraggingDirect, setIsDraggingDirect] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0, posX: 0, posY: 0 });

  // Handle direct drag on the object (auto-select)
  const handleDirectDragStart = (clientX: number, clientY: number) => {
    if (isSelected) return; // Already selected, controls will handle it

    // Auto-select when starting drag
    onSelect();

    setIsDraggingDirect(true);
    setDragStart({
      x: clientX,
      y: clientY,
      posX: item.x,
      posY: item.y
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleDirectDragStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 1) {
      handleDirectDragStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  React.useEffect(() => {
    if (!isDraggingDirect) return;

    const handleMove = (clientX: number, clientY: number) => {
      const deltaX = clientX - dragStart.x;
      const deltaY = clientY - dragStart.y;

      const deltaXPercent = (deltaX / window.innerWidth) * 100;
      const deltaYPercent = (deltaY / window.innerHeight) * 100;

      const newX = Math.max(0, Math.min(100, dragStart.posX + deltaXPercent));
      const newY = Math.max(0, Math.min(100, dragStart.posY + deltaYPercent));

      onUpdate(item.id, { x: newX, y: newY });
    };

    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => setIsDraggingDirect(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDraggingDirect, dragStart, item.id, onUpdate]);

  // Calculate the visual size of the object after scaling
  const visualSize = baseSize * item.scale;

  return (
    <>
      {/* L'objet lui-même */}
      <div
        className="absolute text-6xl select-none opacity-100"
        style={{
          left: `${item.x}%`,
          top: `${item.y}%`,
          transform: `translate(-50%, -50%) scale(${item.scale}) rotate(${item.rotation}deg) translateZ(0)`,
          transformOrigin: 'center center',
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          zIndex: isSelected ? 20 : 10,
          pointerEvents: 'auto',
          cursor: isSelected ? 'default' : 'grab',
          backfaceVisibility: 'hidden',
          WebkitBackfaceVisibility: 'hidden',
          touchAction: 'none'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        <img
          src={objectData.emoji}
          alt={objectData.name}
          className="w-full h-full object-contain"
          style={{
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden'
          }}
        />

        {/* Render instance effects if available, otherwise fallback to legacy flags */}
        {instance?.appliedEffects && instance.appliedEffects.length > 0 ? (
          <ItemEffectsRenderer
            effects={instance.appliedEffects}
            size="medium"
            intensity="medium"
          />
        ) : (
          <>
            {/* Legacy effect rendering (backward compatibility) */}
            <SparkleEffect
              isShiny={objectData.isShiny}
              size="medium"
              intensity="medium"
            />
            <ShinyIndicator
              isShiny={objectData.isShiny}
              size="small"
              position="top-right"
            />
            <BurningEffect
              isBurning={objectData.isBurning}
              size="medium"
              intensity="medium"
            />
          </>
        )}
      </div>

      {/* Bouton de suppression (seulement quand sélectionné) - EN DEHORS du transform */}
      {isSelected && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          className="absolute w-8 h-8 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
          style={{
            left: `calc(${item.x}% + ${visualSize / 2 + 24}px)`,
            top: `calc(${item.y}% - ${visualSize / 2}px)`,
            transform: 'translateX(-50%)',
            zIndex: 25,
            pointerEvents: 'auto'
          }}
          title="Supprimer cet objet"
        >
          <X size={16} />
        </button>
      )}

      {/* Contrôles de transformation (seulement si sélectionné) */}
      {isSelected && (
        <ObjectTransformControls
          position={{ x: item.x, y: item.y }}
          scale={item.scale}
          rotation={item.rotation}
          baseSize={baseSize}
          onPositionChange={(pos) => onUpdate(item.id, pos)}
          onScaleChange={(scale) => onUpdate(item.id, { scale })}
          onRotationChange={(rotation) => onUpdate(item.id, { rotation })}
        />
      )}
    </>
  );
};

// Composant avancé pour le héros avec drag, resize et rotation
const AdvancedDraggableHero: React.FC<{
  heroConfig: HeroConfig;
  selectedHeroData: Hero | undefined;
  isSelected: boolean;
  onSelect: () => void;
  onUpdate: (updates: Partial<HeroConfig>) => void;
}> = ({ heroConfig, selectedHeroData, isSelected, onSelect, onUpdate }) => {
  const baseSize = 384; // Taille de base haute résolution (cohérent avec les objets)
  const [isDraggingDirect, setIsDraggingDirect] = React.useState(false);
  const [dragStart, setDragStart] = React.useState({ x: 0, y: 0, posX: 0, posY: 0 });

  // Handle direct drag on the hero (auto-select)
  const handleDirectDragStart = (clientX: number, clientY: number) => {
    if (isSelected) return; // Already selected, controls will handle it

    // Auto-select when starting drag
    onSelect();

    setIsDraggingDirect(true);
    setDragStart({
      x: clientX,
      y: clientY,
      posX: heroConfig.x,
      posY: heroConfig.y
    });
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    handleDirectDragStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    if (e.touches.length === 1) {
      handleDirectDragStart(e.touches[0].clientX, e.touches[0].clientY);
    }
  };

  React.useEffect(() => {
    if (!isDraggingDirect) return;

    const handleMove = (clientX: number, clientY: number) => {
      const deltaX = clientX - dragStart.x;
      const deltaY = clientY - dragStart.y;

      const deltaXPercent = (deltaX / window.innerWidth) * 100;
      const deltaYPercent = (deltaY / window.innerHeight) * 100;

      const newX = Math.max(0, Math.min(100, dragStart.posX + deltaXPercent));
      const newY = Math.max(0, Math.min(100, dragStart.posY + deltaYPercent));

      onUpdate({ x: newX, y: newY });
    };

    const handleMouseMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 1) {
        e.preventDefault();
        handleMove(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleEnd = () => setIsDraggingDirect(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
    };
  }, [isDraggingDirect, dragStart, onUpdate]);

  return (
    <>
      {/* Le héros lui-même */}
      <div
        className="absolute select-none opacity-100"
        style={{
          left: `${heroConfig.x}%`,
          top: `${heroConfig.y}%`,
          transform: `translate(-50%, -50%) scale(${heroConfig.scale}) rotate(${heroConfig.rotation}deg)`,
          transformOrigin: 'center center',
          width: `${baseSize}px`,
          height: `${baseSize}px`,
          zIndex: isSelected ? 20 : 10,
          pointerEvents: 'auto',
          cursor: isSelected ? 'default' : 'grab',
          touchAction: 'none'
        }}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
      >
        {selectedHeroData?.emoji ? (
          <img
            src={selectedHeroData.emoji}
            alt={selectedHeroData.name || "Hero"}
            className="w-full h-full object-contain"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-6xl">
            🦸
          </div>
        )}
      </div>

      {/* Contrôles de transformation (seulement si sélectionné) */}
      {isSelected && (
        <ObjectTransformControls
          position={{ x: heroConfig.x, y: heroConfig.y }}
          scale={heroConfig.scale}
          rotation={heroConfig.rotation}
          baseSize={baseSize}
          onPositionChange={(pos) => onUpdate(pos)}
          onScaleChange={(scale) => onUpdate({ scale })}
          onRotationChange={(rotation) => onUpdate({ rotation })}
        />
      )}
    </>
  );
};

// Composant pour les overlays déplaçables
const DraggableOverlay: React.FC<{
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  onClose: () => void;
  defaultPosition?: { x: number; y: number };
}> = ({ title, icon, children, onClose, defaultPosition = { x: 100, y: 100 } }) => {
  const [position, setPosition] = useState(defaultPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    setDragStart({
      x: e.clientX - position.x,
      y: e.clientY - position.y
    });
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  return (
    <div
      className="fixed bg-white rounded-lg shadow-xl border-2 border-gray-200 z-50 max-w-sm"
      style={{
        left: position.x,
        top: position.y,
        cursor: isDragging ? 'grabbing' : 'default'
      }}
    >
      <div
        className="flex items-center justify-between p-3 bg-gray-50 rounded-t-lg cursor-grab active:cursor-grabbing border-b"
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2">
          {icon}
          <h3 className="font-semibold text-sm">{title}</h3>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded"
        >
          <X size={16} />
        </button>
      </div>
      <div className="p-3 max-h-64 overflow-y-auto">
        {children}
      </div>
    </div>
  );
};

const WorldScreen: React.FC<WorldScreenProps> = ({
  gameState,
  allObjects,
  userInstances,
  selectedHeroData,
  gradients,
  onBack,
  onEditModeToggle,
  onElementSelect,
  onHeroConfigUpdate,
  onBackgroundConfigUpdate,
  onLogoConfigUpdate,
  onObjectToHome,
  onUpdateHomeObject,
  onRemoveHomeObject,
  onHeroSelect
}) => {
  const {
    homeObjects,
    heroConfig,
    backgroundConfig,
    logoConfig,
    editMode,
    selectedElement,
    collectedObjects,
    selectedHero
  } = gameState;

  const { t } = useTranslation();

  // Charger les héros depuis Supabase
  const { heroes: availableHeroes } = useHeroes();

  // États pour les overlays - ouverts par défaut (MASQUÉS - ancienne version)
  const [showObjectsPanel, setShowObjectsPanel] = useState(false);
  const [showBackgroundPanel, setShowBackgroundPanel] = useState(false);
  const [showHeroPanel, setShowHeroPanel] = useState(false);
  const [showPropertiesPanel, setShowPropertiesPanel] = useState(false);

  // État pour le nouveau menu en bas
  const [activeTab, setActiveTab] = useState<'objects' | 'companions' | 'background' | 'heroes' | 'logo'>('heroes');

  // Load companions from Supabase
  const { companions: gameCompanions } = useCompanions();

  // État pour le mode plein écran du menu
  const [isFullScreen, setIsFullScreen] = useState(false);

  // Détection plateforme native
  const isNative = Capacitor.isNativePlatform();

  // Hook pour le drag & drop
  const { dragState, containerRef, handleMouseDown, handleTouchStart } = useDragAndDrop(
    homeObjects,
    onUpdateHomeObject
  );

  return (
    <div className={`relative h-screen-adaptive bg-gradient-to-b ${gradients[backgroundConfig.gradient][backgroundConfig.intensity]} overflow-hidden select-none`}>
      {/* Bouton retour en haut à gauche */}
      <button
        onClick={onBack}
        className={`absolute z-50 flex items-center gap-1 px-3 py-2 bg-white bg-opacity-90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-opacity-100 transition-all ${
          isNative ? 'ios-back-button' : 'top-6 left-6'
        }`}
      >
        <ChevronLeft size={20} className="text-blue-600" />
        <span className="font-medium text-blue-600">{t("world.back", "Back")}</span>
      </button>

      {/* Logo avec overlay coloré (auto ou manuel) - même position que HomeScreen */}
      <div className={`absolute left-1/2 transform -translate-x-1/2 z-20 ${isNative ? 'ios-logo-secondary' : 'top-8'}`}>
        <div className="relative">
          {/* Logo original */}
          <img
            src="/logo.png"
            alt="Adventures Logo"
            className="h-20 w-auto transition-all duration-700 ease-in-out drop-shadow-lg"
          />
          {/* Couche colorée avec masque et blend mode */}
          <div
            className="absolute inset-0 transition-all duration-700 ease-in-out pointer-events-none"
            style={{
              backgroundColor: logoConfig.color
                ? `${logoConfig.color}B3` // Manual color with 70% opacity (B3 in hex)
                : getLogoOverlayColor(backgroundConfig.gradient, backgroundConfig.intensity), // Auto color
              WebkitMask: 'url(/logo.png) no-repeat center',
              WebkitMaskSize: 'contain',
              mask: 'url(/logo.png) no-repeat center',
              maskSize: 'contain',
              mixBlendMode: 'overlay'
            }}
          />
        </div>
      </div>

      {/* Message d'édition - même position que carousel HomeScreen */}
      <div className={`absolute left-1/2 transform -translate-x-1/2 z-20 ios-carousel ${isNative ? '' : 'top-32'}`}>
        <div className="text-center px-6 py-4 rounded-lg bg-white bg-opacity-90 backdrop-blur-sm shadow-lg max-w-md mx-auto">
          <p className="text-lg font-medium text-gray-800">
            🛠️ {t("world.personnalisez_votre_monde", "Personnalisez votre monde !")}
          </p>
        </div>
      </div>

      {/* Objets flottants sur le fond avec composant avancé */}
      {homeObjects.map(item => {
        const objectData = allObjects.find(obj => obj.id === item.objectId);
        if (!objectData) return null;

        // Lookup the instance if instanceId is available
        const instance = item.instanceId
          ? userInstances.find(inst => inst.id === item.instanceId)
          : undefined;

        return (
          <AdvancedDraggableObject
            key={item.id}
            item={item}
            objectData={objectData}
            instance={instance}
            isSelected={selectedElement?.type === 'object' && selectedElement?.id === item.id}
            onSelect={() => onElementSelect({ type: 'object', id: item.id, data: item })}
            onUpdate={onUpdateHomeObject}
            onRemove={onRemoveHomeObject}
          />
        );
      })}

      {/* Héros avec contrôles avancés */}
      <AdvancedDraggableHero
        heroConfig={heroConfig}
        selectedHeroData={selectedHeroData}
        isSelected={selectedElement?.type === 'hero'}
        onSelect={() => onElementSelect({ type: 'hero', data: heroConfig })}
        onUpdate={onHeroConfigUpdate}
      />

      {/* ANCIEN MENU FLOTTANT - MASQUÉ */}
      <div style={{ display: 'none' }}>
        {/* Boutons d'action flottants */}
        <div className="fixed bottom-6 left-6 flex flex-col gap-3 z-40">
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-3 bg-white bg-opacity-90 backdrop-blur-sm rounded-lg shadow-lg hover:bg-opacity-100 transition-all"
          >
            <ChevronLeft size={20} />
            <span className="font-medium">Retour</span>
          </button>
        </div>

        {/* Menu d'outils flottant */}
        <div className="fixed bottom-6 right-6 flex flex-col gap-3 z-40">
          <button
            onClick={() => setShowObjectsPanel(true)}
            className="p-3 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-600 transition-colors"
            title="Ajouter des objets"
          >
            <Plus size={20} />
          </button>

          <button
            onClick={() => setShowBackgroundPanel(true)}
            className="p-3 bg-purple-500 text-white rounded-full shadow-lg hover:bg-purple-600 transition-colors"
            title="Changer le fond"
          >
            <Palette size={20} />
          </button>

          {selectedElement && (
            <button
              onClick={() => setShowPropertiesPanel(true)}
              className="p-3 bg-blue-500 text-white rounded-full shadow-lg hover:bg-blue-600 transition-colors"
              title="Propriétés"
            >
              <Settings size={20} />
            </button>
          )}
        </div>
      </div>

      {/* NOUVEAU MENU EN BAS - Simplifié avec safe area */}
      <div className={`${
        isFullScreen
          ? 'fixed inset-0 z-50 bg-white'
          : 'absolute bottom-0 left-0 right-0 z-40 bg-white bg-opacity-90 backdrop-blur-sm safe-area-bottom'
      } transition-all duration-300 ${isFullScreen && isNative ? 'safe-area-top' : ''}`}>
        {/* Onglets simplifiés */}
        <div className={`flex border-b border-gray-200 ${isFullScreen && isNative ? 'pt-2' : ''}`}>
          <button
            onClick={() => setActiveTab('heroes')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'heroes'
                ? 'bg-yellow-100 text-yellow-600 border-b-2 border-yellow-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t("world.heroes", "Heroes")}
          </button>
          <button
            onClick={() => setActiveTab('companions')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'companions'
                ? 'bg-pink-100 text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t("world.companions", "Companions")}
          </button>
          <button
            onClick={() => setActiveTab('objects')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'objects'
                ? 'bg-green-100 text-green-600 border-b-2 border-green-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t("world.objects", "Objects")}
          </button>
          <button
            onClick={() => setActiveTab('background')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'background'
                ? 'bg-purple-100 text-purple-600 border-b-2 border-purple-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t("world.background", "Background")}
          </button>
          <button
            onClick={() => setActiveTab('logo')}
            className={`flex-1 py-3 text-sm font-medium transition-colors ${
              activeTab === 'logo'
                ? 'bg-pink-100 text-pink-600 border-b-2 border-pink-600'
                : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t("world.logo", "Logo")}
          </button>
          <button
            onClick={() => setIsFullScreen(!isFullScreen)}
            className="flex items-center justify-center px-4 py-3 text-gray-600 hover:bg-gray-100 transition-colors border-l border-gray-200"
            title={isFullScreen ? t("world.reduire", "Réduire") : t("world.agrandir", "Agrandir")}
          >
            {isFullScreen ? <Minimize2 size={18} /> : <Maximize2 size={18} />}
          </button>
        </div>

        {/* Contenu des onglets */}
        <div className={`p-2 overflow-y-auto ${
          isFullScreen
            ? isNative
              ? 'h-[calc(100vh-60px)] safe-area-bottom'
              : 'h-[calc(100vh-50px)]'
            : 'h-16'
        }`}>
          {activeTab === 'objects' && (
            <div className="flex flex-wrap gap-2 justify-center">
              {(() => {
                // Group instances by baseElementId AND appliedEffects
                const instanceGroups = new Map<string, UserItemInstance[]>();

                userInstances
                  .filter(inst => !inst.archived)
                  .forEach(inst => {
                    // Create unique key: baseElementId + sorted effects
                    const effectsKey = (inst.appliedEffects || []).sort().join(',');
                    const groupKey = `${inst.baseElementId}:${effectsKey}`;

                    if (!instanceGroups.has(groupKey)) {
                      instanceGroups.set(groupKey, []);
                    }
                    instanceGroups.get(groupKey)!.push(inst);
                  });

                // Get IDs of instances already placed
                const placedInstanceIds = new Set(
                  homeObjects.map(obj => obj.instanceId).filter(id => id !== undefined)
                );

                // Render each group as a separate button
                return Array.from(instanceGroups.entries()).map(([groupKey, instances]) => {
                  const firstInstance = instances[0];
                  const objectData = allObjects.find(obj => obj.id === firstInstance.baseElementId);
                  if (!objectData) return null;

                  // Find first available instance in this group
                  const availableInstance = instances.find(
                    inst => !placedInstanceIds.has(inst.id)
                  );

                  // Count available instances in this group
                  const remainingCount = instances.filter(
                    inst => !placedInstanceIds.has(inst.id)
                  ).length;

                  const ownedCount = instances.length;
                  const canPlace = availableInstance !== undefined;
                  const hasEffects = firstInstance.appliedEffects && firstInstance.appliedEffects.length > 0;

                  return (
                    <button
                      key={groupKey}
                      onClick={() => {
                        if (availableInstance) {
                          onObjectToHome(availableInstance.id);
                        }
                      }}
                      disabled={!canPlace}
                      className={`relative w-12 h-12 rounded-lg transition-all border-2 ${
                        canPlace
                          ? 'border-gray-200 hover:scale-110 hover:border-green-400'
                          : 'border-gray-300 opacity-50 cursor-not-allowed'
                      }`}
                      title={`${objectData.name}${hasEffects ? ` (${firstInstance.appliedEffects.join(', ')})` : ''} - ${remainingCount}/${ownedCount} disponible${remainingCount > 1 ? 's' : ''}`}
                    >
                      <div className="relative w-full h-full">
                        <img
                          src={objectData.emoji}
                          alt={objectData.name}
                          className="w-full h-full object-contain p-1"
                        />
                        {/* Render effects on the thumbnail */}
                        {hasEffects && (
                          <div className="absolute inset-0 pointer-events-none">
                            <ItemEffectsRenderer
                              effects={firstInstance.appliedEffects}
                              size="small"
                              intensity="medium"
                            />
                          </div>
                        )}
                      </div>
                      {/* Badge + en haut à droite (dans l'angle) */}
                      {canPlace ? (
                        <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold shadow-md">
                          +
                        </div>
                      ) : (
                        <div className="absolute -top-1 -right-1 bg-gray-400 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold shadow-md">
                          0
                        </div>
                      )}
                      {/* Badge nombre disponible en bas à gauche (dans l'angle) */}
                      <div className="absolute -bottom-1 -left-1 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold shadow-md">
                        {remainingCount}
                      </div>
                    </button>
                  );
                });
              })()}
              {collectedObjects.length === 0 && (
                <div className="text-center py-8 text-gray-500 w-full">
                  <div className="text-4xl mb-2">📚</div>
                  <p className="text-sm">{t("world.lisez_des_livres_pour_collectionner_des_objets", "Lisez des livres pour collectionner des objets !")}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'companions' && (
            <div className="flex flex-wrap gap-2 justify-center">
              {gameCompanions.filter(c => c.unlocked).map(companion => (
                <button
                  key={companion.id}
                  className="relative w-12 h-12 rounded-lg overflow-hidden transition-all border-2 border-pink-200 hover:scale-110 hover:border-pink-400"
                  title={companion.name}
                >
                  <img
                    src={companion.emoji}
                    alt={companion.name}
                    className="w-full h-full object-contain p-1"
                  />
                </button>
              ))}
              {gameCompanions.filter(c => c.unlocked).length === 0 && (
                <div className="text-center py-8 text-gray-500 w-full">
                  <div className="text-4xl mb-2">🐾</div>
                  <p className="text-sm">{t("world.debloquez_des_compagnons_dans_les_histoires", "Débloquez des compagnons dans les histoires !")}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'heroes' && (
            <div className="flex flex-wrap gap-2 justify-center">
              {availableHeroes.map(hero => (
                <button
                  key={hero.id}
                  onClick={() => onHeroSelect?.(hero.id)}
                  className={`relative w-12 h-12 rounded-lg overflow-hidden transition-all border-2 ${
                    selectedHero === hero.id
                      ? 'ring-4 ring-yellow-500 scale-110 border-yellow-500'
                      : 'border-gray-200 hover:scale-110 hover:border-yellow-300'
                  }`}
                  title={hero.name}
                >
                  <img
                    src={hero.emoji}
                    alt={hero.name}
                    className="w-full h-full object-contain p-1"
                  />
                  {selectedHero === hero.id && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-20">
                      <div className="bg-white rounded-full w-4 h-4 flex items-center justify-center text-xs font-bold">✓</div>
                    </div>
                  )}
                  {!hero.unlocked && (
                    <div className="absolute inset-0 bg-gray-500 bg-opacity-60 flex items-center justify-center">
                      <span className="text-lg">🔒</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {activeTab === 'background' && (
            <div className="flex flex-wrap gap-2 justify-center">
              {(Object.keys(gradients) as (keyof typeof gradients)[]).flatMap((gradientKey) =>
                (['light', 'normal', 'dark'] as const).map((intensity) => (
                  <button
                    key={`${gradientKey}-${intensity}`}
                    onClick={() => onBackgroundConfigUpdate({ gradient: gradientKey, intensity })}
                    className={`relative w-12 h-12 rounded-lg overflow-hidden transition-all ${
                      backgroundConfig.gradient === gradientKey && backgroundConfig.intensity === intensity
                        ? 'ring-4 ring-blue-500 scale-110'
                        : 'hover:scale-110'
                    }`}
                    title={`${gradientKey} - ${intensity}`}
                  >
                    <div className={`w-full h-full bg-gradient-to-b ${gradients[gradientKey][intensity]}`}></div>
                    {backgroundConfig.gradient === gradientKey && backgroundConfig.intensity === intensity && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">✓</div>
                      </div>
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {activeTab === 'logo' && (
            <div className="flex flex-wrap gap-2 justify-center">
              {/* Option Auto (basée sur le dégradé) */}
              <button
                onClick={() => onLogoConfigUpdate({ color: null })}
                className={`relative w-12 h-12 rounded-lg overflow-hidden transition-all border-2 ${
                  logoConfig.color === null
                    ? 'ring-4 ring-pink-500 scale-110 border-pink-500'
                    : 'border-gray-200 hover:scale-110 hover:border-pink-300'
                }`}
                title="Auto"
              >
                {/* Mini preview of current gradient */}
                <div className={`w-full h-full bg-gradient-to-b ${gradients[backgroundConfig.gradient][backgroundConfig.intensity]}`}></div>
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-40">
                  <span className="text-white text-xs font-bold">AUTO</span>
                </div>
                {logoConfig.color === null && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">✓</div>
                  </div>
                )}
              </button>

              {/* Palette de couleurs prédéfinies */}
              {[
                { name: 'Rouge', color: '#ef4444' },
                { name: 'Orange', color: '#f97316' },
                { name: 'Jaune', color: '#eab308' },
                { name: 'Vert', color: '#22c55e' },
                { name: 'Cyan', color: '#06b6d4' },
                { name: 'Bleu', color: '#3b82f6' },
                { name: 'Violet', color: '#8b5cf6' },
                { name: 'Rose', color: '#ec4899' },
                { name: 'Gris Foncé', color: '#4b5563' },
                { name: 'Noir', color: '#000000' },
                { name: 'Blanc', color: '#ffffff' },
                { name: 'Or', color: '#fbbf24' },
              ].map(({ name, color }) => (
                <button
                  key={color}
                  onClick={() => onLogoConfigUpdate({ color })}
                  className={`relative w-12 h-12 rounded-lg overflow-hidden transition-all border-2 ${
                    logoConfig.color === color
                      ? 'ring-4 ring-pink-500 scale-110 border-pink-500'
                      : 'border-gray-200 hover:scale-110 hover:border-pink-300'
                  }`}
                  title={name}
                  style={{ backgroundColor: color }}
                >
                  {logoConfig.color === color && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className={`${color === '#ffffff' ? 'bg-black' : 'bg-white'} rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold`}>
                        <span className={color === '#ffffff' ? 'text-white' : 'text-black'}>✓</span>
                      </div>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ANCIENS OVERLAYS - MASQUÉS */}
      <div style={{ display: 'none' }}>
        {/* Overlays déplaçables */}
        {showObjectsPanel && (
          <DraggableOverlay
          title="Ajouter des objets"
          icon={<Plus size={16} className="text-green-500" />}
          onClose={() => setShowObjectsPanel(false)}
          defaultPosition={{ x: Math.min(window.innerWidth - 350, 800), y: 100 }}
        >
          <div className="grid grid-cols-3 gap-2">
            {(() => {
              // Group instances by baseElementId AND appliedEffects
              const instanceGroups = new Map<string, UserItemInstance[]>();

              userInstances
                .filter(inst => !inst.archived)
                .forEach(inst => {
                  const effectsKey = (inst.appliedEffects || []).sort().join(',');
                  const groupKey = `${inst.baseElementId}:${effectsKey}`;
                  if (!instanceGroups.has(groupKey)) {
                    instanceGroups.set(groupKey, []);
                  }
                  instanceGroups.get(groupKey)!.push(inst);
                });

              const placedInstanceIds = new Set(
                homeObjects.map(obj => obj.instanceId).filter(id => id !== undefined)
              );

              return Array.from(instanceGroups.entries()).map(([groupKey, instances]) => {
                const firstInstance = instances[0];
                const objectData = allObjects.find(obj => obj.id === firstInstance.baseElementId);
                if (!objectData) return null;

                const availableInstance = instances.find(
                  inst => !placedInstanceIds.has(inst.id)
                );
                const hasEffects = firstInstance.appliedEffects && firstInstance.appliedEffects.length > 0;

                return (
                  <button
                    key={groupKey}
                    onClick={() => {
                      if (availableInstance) {
                        onObjectToHome(availableInstance.id);
                      }
                    }}
                    disabled={!availableInstance}
                    className="relative p-2 border-2 border-gray-200 rounded-lg hover:border-green-400 hover:bg-green-50 cursor-pointer transition-all"
                    title={`${objectData.name}${hasEffects ? ` (${firstInstance.appliedEffects.join(', ')})` : ''}`}
                  >
                    <div className="mb-1 relative">
                      <img
                        src={objectData.emoji}
                        alt={objectData.name}
                        className="w-8 h-8 mx-auto object-contain"
                      />
                      {hasEffects && (
                        <div className="absolute inset-0 pointer-events-none">
                          <ItemEffectsRenderer
                            effects={firstInstance.appliedEffects}
                            size="small"
                            intensity="medium"
                          />
                        </div>
                      )}
                    </div>
                    <div className="text-xs text-gray-600 truncate">{objectData.name}</div>
                    <div className="absolute -top-1 -right-1 bg-green-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                      +
                    </div>
                  </button>
                );
              });
            })()}
          </div>
          {collectedObjects.length === 0 && (
            <div className="text-center py-6 text-gray-500">
              <div className="text-3xl mb-2">📚</div>
              <p className="text-xs">Lisez des livres pour collectionner des objets !</p>
            </div>
          )}
        </DraggableOverlay>
      )}

      {showBackgroundPanel && (
        <DraggableOverlay
          title="Changer le fond"
          icon={<Palette size={16} className="text-purple-500" />}
          onClose={() => setShowBackgroundPanel(false)}
          defaultPosition={{ x: 100, y: 200 }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Thème</label>
              <div className="grid grid-cols-2 gap-2">
                {(Object.keys(gradients) as (keyof typeof gradients)[]).map((gradientKey) => (
                  <button
                    key={gradientKey}
                    onClick={() => onBackgroundConfigUpdate({ 
                      ...backgroundConfig, 
                      gradient: gradientKey
                    })}
                    className={`relative h-8 rounded-lg overflow-hidden transition-all ${
                      backgroundConfig.gradient === gradientKey
                        ? 'ring-2 ring-blue-500'
                        : 'hover:scale-105'
                    }`}
                  >
                    <div className={`w-full h-full bg-gradient-to-b ${gradients[gradientKey as keyof typeof gradients].normal}`}></div>
                    {backgroundConfig.gradient === gradientKey && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="bg-white rounded-full w-4 h-4 flex items-center justify-center text-xs">✓</div>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Intensité</label>
              <div className="flex gap-1">
                {(['light', 'normal', 'dark'] as const).map((intensity) => (
                  <button
                    key={intensity}
                    onClick={() => onBackgroundConfigUpdate({ ...backgroundConfig, intensity })}
                    className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-all ${
                      backgroundConfig.intensity === intensity
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {intensity === 'light' ? 'Clair' : intensity === 'normal' ? 'Normal' : 'Sombre'}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </DraggableOverlay>
      )}

      {showPropertiesPanel && selectedElement && (
        <DraggableOverlay
          title={selectedElement.type === 'hero' ? 'Héros' : 'Objet'}
          icon={<Settings size={16} className="text-blue-500" />}
          onClose={() => setShowPropertiesPanel(false)}
          defaultPosition={{ x: 50, y: 300 }}
        >
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium mb-1">Position</label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <span className="text-xs text-gray-500">X: {Math.round(selectedElement.data.x)}%</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={selectedElement.data.x}
                    onChange={(e) => {
                      const newX = parseInt(e.target.value);
                      if (selectedElement.type === 'hero') {
                        onHeroConfigUpdate({ x: newX });
                      } else {
                        onUpdateHomeObject(selectedElement.id!, { x: newX });
                      }
                    }}
                    className="w-full"
                  />
                </div>
                <div>
                  <span className="text-xs text-gray-500">Y: {Math.round(selectedElement.data.y)}%</span>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={selectedElement.data.y}
                    onChange={(e) => {
                      const newY = parseInt(e.target.value);
                      if (selectedElement.type === 'hero') {
                        onHeroConfigUpdate({ y: newY });
                      } else {
                        onUpdateHomeObject(selectedElement.id!, { y: newY });
                      }
                    }}
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">
                Taille: {Math.round(selectedElement.data.scale * 100)}%
              </label>
              <input
                type="range"
                min="0.3"
                max="2"
                step="0.1"
                value={selectedElement.data.scale}
                onChange={(e) => {
                  const newScale = parseFloat(e.target.value);
                  if (selectedElement.type === 'hero') {
                    onHeroConfigUpdate({ scale: newScale });
                  } else {
                    onUpdateHomeObject(selectedElement.id!, { scale: newScale });
                  }
                }}
                className="w-full"
              />
            </div>

            <div>
              <label className="block text-xs font-medium mb-1">
                Rotation: {Math.round(selectedElement.data.rotation)}°
              </label>
              <input
                type="range"
                min="0"
                max="360"
                value={selectedElement.data.rotation}
                onChange={(e) => {
                  const newRotation = parseInt(e.target.value);
                  if (selectedElement.type === 'hero') {
                    onHeroConfigUpdate({ rotation: newRotation });
                  } else {
                    onUpdateHomeObject(selectedElement.id!, { rotation: newRotation });
                  }
                }}
                className="w-full"
              />
            </div>

            {selectedElement.type === 'object' && (
              <button
                onClick={() => {
                  onRemoveHomeObject(selectedElement.id!);
                  onElementSelect(null);
                  setShowPropertiesPanel(false);
                }}
                className="w-full py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors text-xs"
              >
                Supprimer
              </button>
            )}
          </div>
        </DraggableOverlay>
      )}
      </div>
    </div>
  );
};

export default WorldScreen;